/**
 * A geometria da grade do calendário, sem React: fatiar acontecimentos por semana
 * e empilhar sobreposições em faixas.
 *
 * <p>Morava dentro de `GradeDaSeason.tsx`. Saiu de lá porque é a parte que quebra
 * em silêncio — um erro de recorte na virada da semana ou uma faixa trocada não
 * derruba o build, só desenha o calendário errado — e ser função pura é o que a
 * deixa testável sem montar componente.
 *
 * <p>As funções aceitam qualquer coisa com `{ id, diaInicio, diaFim }` em vez de
 * exigir o `Acontecimento` inteiro: o componente passa o dele, e o teste não
 * precisa inventar refs e autoria para exercitar geometria.
 */

export interface ItemDeGrade {
  id: number;
  diaInicio: number;
  diaFim: number;
}

export interface Segmento<T extends ItemDeGrade = ItemDeGrade> {
  acontecimento: T;
  /** Dias absolutos deste pedaço (já recortados na semana). */
  inicio: number;
  fim: number;
  continuaEsq: boolean;
  continuaDir: boolean;
  faixa: number;
}

export const semanaDo = (dia: number) => Math.floor((dia - 1) / 7);
export const colunaDo = (dia: number) => ((dia - 1) % 7) + 1;

/** Fatia os acontecimentos por semana e distribui as faixas de empilhamento. */
export function montarSemanas<T extends ItemDeGrade>(
  diasTotal: number,
  acontecimentos: T[]
): Segmento<T>[][] {
  const totalSemanas = Math.ceil(diasTotal / 7);
  const semanas: Segmento<T>[][] = Array.from({ length: totalSemanas }, () => []);

  // Ordem global estável: a mesma barra tende à mesma faixa em semanas vizinhas.
  const ordenados = [...acontecimentos].sort(
    (a, b) =>
      a.diaInicio - b.diaInicio ||
      b.diaFim - b.diaInicio - (a.diaFim - a.diaInicio) ||
      a.id - b.id
  );

  for (const acontecimento of ordenados) {
    for (let w = semanaDo(acontecimento.diaInicio); w <= semanaDo(acontecimento.diaFim); w++) {
      const inicio = Math.max(acontecimento.diaInicio, w * 7 + 1);
      const fim = Math.min(acontecimento.diaFim, w * 7 + 7);
      semanas[w].push({
        acontecimento,
        inicio,
        fim,
        continuaEsq: acontecimento.diaInicio < inicio,
        continuaDir: acontecimento.diaFim > fim,
        faixa: 0,
      });
    }
  }

  // Partição de intervalos por semana: primeira faixa cujo último dia já passou.
  for (const segmentos of semanas) {
    const ultimoDiaDaFaixa: number[] = [];
    for (const segmento of segmentos) {
      let faixa = ultimoDiaDaFaixa.findIndex((ultimo) => ultimo < segmento.inicio);
      if (faixa === -1) {
        faixa = ultimoDiaDaFaixa.length;
        ultimoDiaDaFaixa.push(0);
      }
      segmento.faixa = faixa;
      ultimoDiaDaFaixa[faixa] = segmento.fim;
    }
  }

  return semanas;
}
